import PageLayout from 'components/pages/PageLayout'
import React, { useState } from 'react'
import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import createApolloClient from 'components/withApollo/createApolloClient'
import ErrorPage from 'components/pages/ErrorPage'
import CheckBoxGroup from 'components/ui/CheckBoxGroup'
import LoadingPage from 'components/pages/LoadingPage'
import StarsRating from 'components/StarsRating'
import { usePagination } from 'url-pagination'
import Loading from 'components/Loading'
import ButtonLink from 'components/ui/ButtonLink'
import { AdjustmentsIcon } from '@heroicons/react/outline'
import Button from 'components/ui/Button'
import { MapIcon, PlusIcon, SearchIcon } from '@heroicons/react/solid'
import TextField from 'components/ui/TextField'

import { useTrucksListPageQuery } from './types/query'
import Trucks from './Trucks'
import {
  TrucksListPathsQueryDocument,
  TrucksListPathsQuery
} from './types/TrucksListPaths'

type Props = {}

type Params = {
  market: string
}

export type FormValues = {
  foodType: number
}

const ratingOptions = [
  {
    label: (
      <span className="flex items-center gap-x-2">
        <span>from 4.5</span>
        <StarsRating scale={1.5} stars={4.5} />
      </span>
    ),
    value: 4.5
  },
  {
    label: (
      <span className="flex items-center gap-x-2">
        <span>from 4</span>
        <StarsRating scale={1.5} stars={4} />
      </span>
    ),
    value: 4
  },
  {
    label: 'All trucks',
    value: 0
  }
]

type FiltersProps = {
  foodTypesOptions: { label: string; value: number }[]
  selectedFoodTypeIds: number[]
  onChangeFoodTypes: (value: number) => void
  selectedRating: number | null
  setSelectedRating: (value: number) => void
}
const Filters = ({
  foodTypesOptions,
  selectedFoodTypeIds,
  onChangeFoodTypes,
  selectedRating,
  setSelectedRating
}: FiltersProps) => {
  const [search, setSearch] = useState('')

  const filteredFoodTypes = foodTypesOptions.filter((foodType) =>
    foodType.label.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex flex-col gap-y-4 items-stretch">
      <div>
        <p className="font-semibold">Сuisine</p>
        <div className="mt-2 mb-4">
          <TextField
            iconRight={<SearchIcon />}
            name="Search Сuisine"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cuisines by name..."
            value={search}
          />
        </div>
        <div
          className="max-h-[50vh] lg:max-h-72 px-2 my-2 scrollbar-always-visible overflow-auto"
          style={{
            scrollbarWidth: 'auto',
            msOverflowStyle: 'auto'
          }}
        >
          {filteredFoodTypes.length > 0 ? (
            <CheckBoxGroup
              name="foodTypes"
              onChange={(value) => onChangeFoodTypes(value)}
              options={filteredFoodTypes}
              value={selectedFoodTypeIds}
            />
          ) : (
            <div className="text-gray-400">Nothing found</div>
          )}
        </div>
      </div>

      <div>
        <p className="font-semibold">Rating</p>
        <div className="px-2 my-2">
          <CheckBoxGroup
            name="rating"
            onChange={(value) => setSelectedRating(value)}
            options={ratingOptions}
            value={selectedRating ? [selectedRating] : null}
          />
        </div>
      </div>
    </div>
  )
}

const SelectedFilterOptionContainer = ({
  onClick,
  children
}: {
  onClick: () => void
  children: string
}) => {
  return (
    <div
      aria-label={`Remove ${children} filter`}
      className="pl-4 pr-6 py-2 bg-gray-100 rounded-md max-w-max relative group hover:bg-gray-200 transition-colors duration-200 ease-in-out cursor-pointer"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick()
      }}
      role="button"
      tabIndex={0}
    >
      <div className="text-xs font-semibold text-gray-500 group-hover:text-gray-600">
        {children}
      </div>
      <div className="absolute top-1 right-1">
        <PlusIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-500 rotate-45 transition-colors duration-200 ease-in-out" />
      </div>
    </div>
  )
}

const TrucksPage: NextPage = () => {
  const router = useRouter()
  const { market: slug } = router.query as Params

  const pagination = usePagination({ perPage: 10 })

  const [selectedFoodTypeIds, setSelectedFoodTypeIds] = useState<number[]>([])
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [showFilters, setShowFilters] = useState<boolean>(false)

  const { data: currentData, error, previousData } = useTrucksListPageQuery({
    fetchPolicy: 'cache-first',
    variables: {
      slug,
      trucksOffset: pagination.offset,
      trucksLimit: pagination.perPage,
      foodTypeIds: selectedFoodTypeIds?.sort() || null,
      minRating: selectedRating || null
    }
  })

  const data = currentData ?? previousData

  if (error) return <ErrorPage message={error.message} />
  if (!data || !data.marketBySlug || !data.foodTypes) {
    return <LoadingPage />
  }
  const market = data.marketBySlug
  const foodTypesOptions = data!.foodTypes.map((foodType) => ({
    label: foodType.name as string,
    value: foodType.id
  }))
  const onChangeFoodTypes = (id: number) => {
    let newValue = []
    if (selectedFoodTypeIds !== []) {
      if (selectedFoodTypeIds.includes(id)) {
        newValue = selectedFoodTypeIds.filter((a) => a !== id)
      } else {
        newValue = [...selectedFoodTypeIds, id]
      }
    } else {
      newValue = [id]
    }
    setSelectedFoodTypeIds(newValue)
  }

  return (
    <PageLayout fullWidth staticPage>
      <PageMeta
        canonicalPath={market.customerTrucksListUrl}
        coordinates={[market.latitude, market.longitude]}
        image={market.image.medium}
        keywords={`food trucks list, ${market.name}, ${market.stateFull}, catering`}
        ldJson={data.structuredData.marketListTrucksPage}
        title={`Best Food Trucks | Browse food trucks in ${market.name}`}
      />
      <div className="relative max-w-7xl w-full px-4 lg:my-12 my-6">
        {showFilters && (
          <div className="fixed z-10 inset-0 bg-white flex flex-col justify-between px-4 pt-20 pb-10">
            <Filters
              foodTypesOptions={foodTypesOptions}
              onChangeFoodTypes={onChangeFoodTypes}
              selectedFoodTypeIds={selectedFoodTypeIds}
              selectedRating={selectedRating}
              setSelectedRating={setSelectedRating}
            />
            <div className="mt-4">
              <Button
                color="primary"
                onClick={() => {
                  setShowFilters(false)
                }}
                variant="outline"
                fullWidth
              >
                {`Show ${data.marketBySlug.trucks.total} trucks`}
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-x-6 gap-y-2">
          <ButtonLink
            color="primary"
            href="/hire-food-truck"
            variant="contained"
          >
            <p className="text-center">Hire a food truck in {market.name}</p>
          </ButtonLink>

          <ButtonLink
            color="secondary"
            href={`/food-trucks/catering/${slug}`}
            variant="outline"
          >
            <p className="text-center"> Learn about {market.name} catering</p>
          </ButtonLink>
          <ButtonLink
            className="text-center"
            color="secondary"
            href={`/food-trucks/${slug}`}
            variant="outline"
          >
            {market.name} &amp; Food Trucks
          </ButtonLink>
          <ButtonLink
            className="text-center md:ml-auto"
            color="primary"
            href={
              market.showMap ? market.customerTrucksMapPath : '/food-trucks-map'
            }
            variant="outline"
          >
            <MapIcon className="w-5 h-5 mr-2" />
            <span>View food trucks on map</span>
          </ButtonLink>
        </div>

        <h1 className="text-3xl mt-10 mb-2 font-bold">
          Browse all food trucks in {market.name}:
        </h1>

        <div>
          <div className="lg:hidden">
            <button
              className="w-full border border-solid border-gray-300 bg-gray-100 flex justify-between px-4 py-2 rounded-lg mb-2 active:ring-1 active:ring-offset-1 active:ring-gray-500"
              onClick={() => setShowFilters(true)}
              type="button"
            >
              <p className="font-semibold text-gray-800">Filters</p>{' '}
              <AdjustmentsIcon className="w-5 h-5 text-gray-700 rotate-90" />
            </button>
          </div>

          <div className="flex flex-wrap gap-y-2 gap-x-4 my-4">
            {!!selectedRating && selectedRating > 0 && (
              <SelectedFilterOptionContainer
                onClick={() => setSelectedRating(null)}
              >
                {`${selectedRating} stars`}
              </SelectedFilterOptionContainer>
            )}
            {selectedFoodTypeIds.map((foodTypeId) => (
              <SelectedFilterOptionContainer
                key={foodTypeId}
                onClick={() =>
                  setSelectedFoodTypeIds([
                    ...selectedFoodTypeIds.filter((f) => f !== foodTypeId)
                  ])
                }
              >
                {foodTypesOptions.find((f) => f.value === foodTypeId)!.label}
              </SelectedFilterOptionContainer>
            ))}
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-12 mt-4">
          <div className="hidden lg:block lg:col-span-3 pr-14">
            <Filters
              foodTypesOptions={foodTypesOptions}
              onChangeFoodTypes={onChangeFoodTypes}
              selectedFoodTypeIds={selectedFoodTypeIds}
              selectedRating={selectedRating}
              setSelectedRating={setSelectedRating}
            />
          </div>

          <div className="lg:col-span-9">
            {data?.marketBySlug?.trucks ? (
              <Trucks
                pagination={pagination}
                trucks={data?.marketBySlug?.trucks}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <Loading />
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default withApollo({
  ssr: false
})(TrucksPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<TrucksListPathsQuery>({
    query: TrucksListPathsQueryDocument
  })

  const paths = data.customerPaths.marketTrucksListPaths.map((marketPath) => ({
    params: { market: marketPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(TrucksPage, {
  revalidate: 7 * 24 * 60 * 60 // 7 days
})
